import { sortTaxonomyByLabel } from "@/core/services/taxonomy.sort";
import type { ArtisticPeriod } from "@/core/services/types/taxonomy.types";

export type ArtisticPeriodGroup = {
  parent: ArtisticPeriod;
  children: ArtisticPeriod[];
};

export type ArtisticPeriodSelectGroup = {
  id: string;
  parent: { id: string; label: string };
  children: { id: string; label: string }[];
};

function isArtisticPeriodParent(period: ArtisticPeriod): boolean {
  return period.parent_id == null;
}

export function buildArtisticPeriodGroups(
  periods: ArtisticPeriod[],
): ArtisticPeriodGroup[] {
  const roots = sortTaxonomyByLabel(periods.filter(isArtisticPeriodParent));

  const childrenByParentId = new Map<string, ArtisticPeriod[]>();
  for (const period of periods) {
    if (!period.parent_id) continue;
    const siblings = childrenByParentId.get(period.parent_id) ?? [];
    siblings.push(period);
    childrenByParentId.set(period.parent_id, siblings);
  }

  return roots.map((parent) => ({
    parent,
    children: sortTaxonomyByLabel(childrenByParentId.get(parent.id) ?? []),
  }));
}

export function buildArtisticPeriodSelectGroups(
  periods: ArtisticPeriod[],
): ArtisticPeriodSelectGroup[] {
  return buildArtisticPeriodGroups(periods).map((group) => ({
    id: group.parent.id,
    parent: { id: group.parent.id, label: group.parent.label },
    children: group.children.map((child) => ({
      id: child.id,
      label: child.label,
    })),
  }));
}

export function flattenArtisticPeriodSelectGroups(
  groups: ArtisticPeriodSelectGroup[],
): { id: string; label: string }[] {
  return groups.flatMap((group) => [group.parent, ...group.children]);
}
